import { StyledAlert, PaddedSurface as Surface } from "App/global-style";
import { FormikProps, useField } from "formik";
import React, { ChangeEvent, useState } from "react";
import {
  BicycleRegistryDescription,
  Fieldset,
  Hint,
  Label,
  LabelText,
  Legend,
  OptionContainer,
  Radio,
} from "./styled";
import { productOptions, setAllFieldsTouched } from "./utils";

import { Icon } from "@storeblocks/icon";
import { ContactUsAlert } from "Components/Form/Alert";
import { useTexts } from "Components/TextContext";
import { AlertTitle } from "Components/TextContext/commonComponentEnums";
import { ProductCategory } from "Features/CheckPrice/Products/RiskProducts/Valuables/valuablesConstants";
import { ProductIntroFormValues } from "Features/CheckPrice/Products/RiskProducts/Valuables/valuablesInterface";
import { getProductLabel } from "Features/CheckPrice/Products/RiskProducts/Valuables/valuablesUtils/getProductLabel";
import { getError } from "Utils/productUtils";
import ReactMarkdown from "react-markdown";

interface Props {
  formProps: FormikProps<ProductIntroFormValues>;
}

export const Products = (props: Props): React.ReactElement => {
  const { formProps } = props;
  const { getText } = useTexts();
  const [fieldProps] = useField({ name: "product" });
  const [componentId] = useState("product-radio-group");

  const category: ProductCategory = formProps.values.product;

  const changeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    setAllFieldsTouched(formProps, false);
    fieldProps.onChange(event);
  };

  const errorMessage = getError("product", formProps);
  const requiredError = getText("valuablesform.validation.product-required");
  const displayError = errorMessage === requiredError;

  return (
    <>
      <Surface>
        <Fieldset>
          <Legend>{getText("valuablesform.label.product")}</Legend>
          <Hint>{getText("valuablesform.hint.product")}</Hint>
          <OptionContainer>
            {productOptions.map((option, index) => (
              <Label
                key={`product-option-${option.value}`}
                checked={fieldProps.value === option.value}
              >
                <Radio
                  type="radio"
                  {...fieldProps}
                  value={option.value}
                  checked={fieldProps.value === option.value}
                  onChange={changeHandler}
                  aria-describedby={
                    displayError && index === 0
                      ? `${componentId}-error`
                      : undefined
                  }
                />
                <LabelText>{getProductLabel(getText, option.value)}</LabelText>
                <Icon name={option.icon} />
              </Label>
            ))}
          </OptionContainer>
        </Fieldset>

        {displayError && (
          <StyledAlert
            id={`${componentId}-error`}
            title={errorMessage}
            type="error"
          />
        )}
      </Surface>

      {category === ProductCategory.OTHER && (
        <ContactUsAlert
          title={getText(AlertTitle.CALL_US)}
          description={getText(
            "valuablesform.alertdescription.unsupportedproduct",
          )}
          alertId="unsupportedValuable"
          variant="border"
          marginBottom="32px"
        />
      )}

      {[ProductCategory.BIKE, ProductCategory.EL_BIKE].includes(category) && (
        <StyledAlert alertId="bicycleRegistryNote" title="" transparent>
          <BicycleRegistryDescription>
            <ReactMarkdown linkTarget="_blank">
              {getText("valuablesform.alerttitle.bicycle_registry_markdown")}
            </ReactMarkdown>
            {ProductCategory.EL_BIKE === category && (
              <span>
                <br />
                <br />
                {getText("valuablesform.alerttitle.bicycle_motor_capacity")}
              </span>
            )}
          </BicycleRegistryDescription>
        </StyledAlert>
      )}
    </>
  );
};
